import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UsersThree, Phone, EnvelopeSimple, IdentificationCard, Buildings, User } from "@phosphor-icons/react";
import { BR_STATES } from "@/lib/br";
import { CityAutocomplete } from "@/components/CityAutocomplete";

const EMPTY = {
  name: "", doc_type: "cnpj", doc_number: "", client_type: "transportadora",
  contact_name: "", phone: "", email: "",
  city: "", uf: "", address: "", notes: "",
};

const formatDoc = (type, num) => {
  if (!num) return "-";
  const n = num.replace(/\D/g, "");
  if (type === "cnpj" && n.length === 14) return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8,12)}-${n.slice(12)}`;
  if (type === "cpf" && n.length === 11) return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}-${n.slice(9)}`;
  return num;
};

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get("/clients");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const h = (e) => { if (e.detail?.entity === "client") load(); };
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente cadastrado");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir cliente? Fretes já cadastrados não serão afetados.")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Cliente removido"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const startEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };
  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  const filtered = items.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.doc_number, c.city, c.uf, c.contact_name, c.phone].join(" ").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
            <UsersThree size={32} weight="duotone" className="text-blue-600" />
            <span>Clientes</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Cadastro de transportadoras e clientes particulares para quem você presta serviço</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="🔎 Buscar por nome, cidade, CNPJ..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="client-search" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startNew} data-testid="btn-new-client" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5">
                <Plus size={16} /> Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><UsersThree size={22} weight="duotone" className="text-blue-600" />{editing ? "Editar" : "Novo"} Cliente</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nome / Razão Social *</Label><Input value={form.name} onChange={set("name")} required placeholder="Ex: Transportes ABC Ltda" data-testid="client-name" /></div>

                <div><Label>Tipo</Label>
                  <Select value={form.client_type} onValueChange={set("client_type")}>
                    <SelectTrigger data-testid="client-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transportadora"><span className="inline-flex items-center gap-2"><Buildings size={14} /> Transportadora</span></SelectItem>
                      <SelectItem value="particular"><span className="inline-flex items-center gap-2"><User size={14} /> Cliente Particular</span></SelectItem>
                      <SelectItem value="fornecedor"><span className="inline-flex items-center gap-2"><Buildings size={14} /> Fornecedor</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Documento</Label>
                  <div className="flex gap-2">
                    <Select value={form.doc_type} onValueChange={set("doc_type")}>
                      <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cnpj">CNPJ</SelectItem><SelectItem value="cpf">CPF</SelectItem></SelectContent>
                    </Select>
                    <Input value={form.doc_number} onChange={set("doc_number")} placeholder={form.doc_type === "cnpj" ? "00.000.000/0000-00" : "000.000.000-00"} className="flex-1" data-testid="client-doc-number" />
                  </div>
                </div>

                <div className="col-span-2 mt-2 text-xs font-bold uppercase tracking-wider text-blue-700">Contato</div>
                <div><Label>Nome do contato</Label><Input value={form.contact_name} onChange={set("contact_name")} placeholder="Ex: João da Silva" /></div>
                <div><Label>Telefone / WhatsApp</Label><Input value={form.phone} onChange={set("phone")} placeholder="(65) 9 9999-9999" data-testid="client-phone" /></div>
                <div className="col-span-2"><Label>E-mail</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="contato@empresa.com" /></div>

                <div className="col-span-2 mt-2 text-xs font-bold uppercase tracking-wider text-blue-700">Localização</div>
                <div><Label>UF</Label>
                  <Select value={form.uf} onValueChange={set("uf")}>
                    <SelectTrigger data-testid="client-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Cidade</Label>
                  <CityAutocomplete value={form.city} uf={form.uf} onChange={set("city")} placeholder="Digite a cidade" data-testid="client-city" />
                </div>
                <div className="col-span-2"><Label>Endereço (opcional)</Label><Input value={form.address} onChange={set("address")} placeholder="Rua, número, bairro" /></div>

                <div className="col-span-2"><Label>Observações</Label><Input value={form.notes} onChange={set("notes")} placeholder="Ex: Cliente prefere pagar na quinta-feira" /></div>

                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid="btn-save-client" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">{editing ? "Salvar" : "Cadastrar"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Meus clientes ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">
              {items.length === 0
                ? "Nenhum cliente cadastrado ainda. Clique em \"Novo Cliente\" para começar."
                : "Nenhum cliente encontrado com esse filtro."}
            </p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Cidade/UF</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                    <TableCell>
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                        {c.client_type === "transportadora" ? <><Buildings size={12} /> Transportadora</> : c.client_type === "fornecedor" ? <><Buildings size={12} /> Fornecedor</> : <><User size={12} /> Particular</>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="uppercase text-slate-500">{c.doc_type}</div>
                      <div className="font-mono">{formatDoc(c.doc_type, c.doc_number)}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.contact_name && <div>{c.contact_name}</div>}
                      {c.phone && <div className="inline-flex items-center gap-1 text-slate-600"><Phone size={12} /> {c.phone}</div>}
                      {c.email && <div className="inline-flex items-center gap-1 text-slate-600"><EnvelopeSimple size={12} /> {c.email}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{c.city}{c.uf ? `/${c.uf}` : ""}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(c)} data-testid={`btn-edit-client-${c.id}`}><PencilSimple size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`btn-delete-client-${c.id}`}><Trash size={16} className="text-red-600" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
